import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  tmsApi,
  type OrderTaskResource,
  type ReplaceLoadResourcesRequest,
  type ReplaceOrderResourcesRequest,
  type ReplaceOrderTaskResourcesRequest,
  type ReplaceTasksResourcesRequest,
} from '../client';
import { loadKeys } from './useLoads';

/**
 * Stable query-key factory for assignment caches. Keyed by order, matching the
 * only read endpoint — a task's assignments are always fetched as part of its
 * order.
 */
export const orderTaskResourceKeys = {
  all: ['tms', 'orderTaskResource'] as const,
  byOrder: (orderId: string) => ['tms', 'orderTaskResource', 'order', orderId] as const,
  byOrganisation: (organisationId: string) =>
    ['tms', 'orderTaskResource', 'organisation', organisationId] as const,
};

/**
 * Every assignment in the organisation, for the planning queue.
 *
 * <p>
 * One request instead of a per-order fan-out — the queue shows every order, so
 * fetching assignments order-by-order would multiply the page's request count by
 * the number of rows.
 * </p>
 */
export function useOrganisationResources(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<OrderTaskResource[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderTaskResource[], Error>({
    queryKey: organisationId
      ? orderTaskResourceKeys.byOrganisation(organisationId)
      : orderTaskResourceKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getOrganisationResources(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** Every assignment across an order's tasks. Pass `null` to keep it disabled. */
export function useOrderResources(
  orderId: string | null,
  options?: Omit<UseQueryOptions<OrderTaskResource[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderTaskResource[], Error>({
    queryKey: orderId ? orderTaskResourceKeys.byOrder(orderId) : orderTaskResourceKeys.all,
    queryFn: () => {
      if (!orderId) throw new Error('No order id');
      return tmsApi.getOrderResources(orderId);
    },
    enabled: !!orderId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Replace one task's assignments.
 *
 * <p>
 * Also invalidates the load caches: a load's crew is derived from its tasks'
 * assignments, so the manifest board and the plan queue are stale the moment this
 * lands. Missing that would leave the Manifest showing the previous driver.
 * </p>
 */
export function useReplaceOrderTaskResources() {
  const qc = useQueryClient();
  return useMutation<
    OrderTaskResource[],
    Error,
    { orderId: string; body: ReplaceOrderTaskResourcesRequest }
  >({
    mutationKey: [...orderTaskResourceKeys.all, 'replaceTask'],
    mutationFn: ({ body }) => tmsApi.replaceOrderTaskResources(body),
    onSuccess: (assignments, { orderId }) => {
      qc.setQueryData(orderTaskResourceKeys.byOrder(orderId), assignments);
      // The org-wide queue cache and the load projections both went stale.
      qc.invalidateQueries({ queryKey: orderTaskResourceKeys.all });
      qc.invalidateQueries({ queryKey: loadKeys.all });
    },
  });
}

/**
 * Replace several tasks' assignments at once — the cascade.
 *
 * <p>
 * Can span orders (a load's stops do), so it can't seed one order's cache from the
 * response; it invalidates every assignment cache alongside the loads.
 * </p>
 */
export function useReplaceTasksResources() {
  const qc = useQueryClient();
  return useMutation<OrderTaskResource[], Error, ReplaceTasksResourcesRequest>({
    mutationKey: [...orderTaskResourceKeys.all, 'replaceTasks'],
    mutationFn: (body) => tmsApi.replaceTasksResources(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderTaskResourceKeys.all });
      qc.invalidateQueries({ queryKey: loadKeys.all });
    },
  });
}

/** Replace the assignment set across every task on an order. */
export function useReplaceOrderResources() {
  const qc = useQueryClient();
  return useMutation<OrderTaskResource[], Error, ReplaceOrderResourcesRequest>({
    mutationKey: [...orderTaskResourceKeys.all, 'replaceOrder'],
    mutationFn: (body) => tmsApi.replaceOrderResources(body),
    onSuccess: (assignments, { orderId }) => {
      qc.setQueryData(orderTaskResourceKeys.byOrder(orderId), assignments);
      qc.invalidateQueries({ queryKey: orderTaskResourceKeys.all });
      qc.invalidateQueries({ queryKey: loadKeys.all });
    },
  });
}

/**
 * Replace the assignment set across every task a load touches.
 *
 * <p>
 * A load can span several orders, so this can't seed one order's cache from the
 * response — it invalidates all assignment caches instead, alongside the loads.
 * </p>
 */
export function useReplaceLoadResources() {
  const qc = useQueryClient();
  return useMutation<OrderTaskResource[], Error, ReplaceLoadResourcesRequest>({
    mutationKey: [...orderTaskResourceKeys.all, 'replaceLoad'],
    mutationFn: (body) => tmsApi.replaceLoadResources(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderTaskResourceKeys.all });
      qc.invalidateQueries({ queryKey: loadKeys.all });
    },
  });
}
